import React, { useState, useEffect } from 'react';
import { useApp } from '../../../context/AppContext';
import { Card, Btn, Badge, Chip, SectionHead, Table, Tr, Td, Modal, FormGroup, Textarea, InfoBanner, Input } from '../../common/UI';
import { generateStocklistPDF, generateOfflandPDF, generateCompletionPDF } from '../../../utils/pdfGenerator';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function StockList({ onNav }) {
  const { 
    stock, role, approveStock, rejectStock, updateStock, deleteStock, completeStock, 
    showToast, deleteDraft, activeStock, completedStock,
    customers: allCustomers, vessels: allVessels, suppliers: allSuppliers, 
    locations: allLocations, freightTypes: allFreight, countries: allCountries
  } = useApp();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filterVessel, setFilterVessel] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [selected, setSelected] = useState([]);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completePhotos, setCompletePhotos] = useState([]);
  const [completeDocs, setCompleteDocs] = useState([]);
  const [signedBy, setSignedBy] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }
  const [editTarget, setEditTarget] = useState(null);
  const [editData, setEditData] = useState({});
  const [isBulk, setIsBulk] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const handleCompletePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (completePhotos.length + files.length > 10) {
      showToast('Maximum 10 photos allowed', 'error'); return;
    }
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCompletePhotos(p => [...p, {
          id: Date.now() + Math.random(),
          name: file.name,
          preview: ev.target.result,
          size: (file.size / 1024).toFixed(1) + ' KB'
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };
  const removeCompletePhoto = (id) => setCompletePhotos(p => p.filter(x => x.id !== id));

  const handleCompleteDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCompleteDocs(p => [...p, {
          id: Date.now() + Math.random(),
          file: file,
          name: file.name,
          size: (file.size / 1024).toFixed(1) + ' KB'
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };
  const removeCompleteDoc = (id) => setCompleteDocs(p => p.filter(x => x.id !== id));

  const vessels = [...new Set(stock.map(s => s.vessel))];
  const customers = [...new Set(stock.map(s => s.customer))];

  const filtered = stock.filter(s => {
    const q = search.toLowerCase();
    const matchQ = !q ||
      (s.po || '').toLowerCase().includes(q) ||
      (s.customer || '').toLowerCase().includes(q) ||
      (s.vessel || '').toLowerCase().includes(q) ||
      (s.awb || '').toLowerCase().includes(q) ||
      (s.description || '').toLowerCase().includes(q);

    let matchTab = false;
    if (activeTab === 'all') matchTab = true;
    else if (activeTab === 'active') matchTab = s.status === 'active';
    else if (activeTab === 'completed') matchTab = s.status === 'completed';
    else if (activeTab === 'offland') matchTab = s.type === 'Offland';
    else if (activeTab === 'drafted') matchTab = s.status === 'drafted';
    else matchTab = s.status === activeTab;

    const matchV = filterVessel === 'all' || s.vessel === filterVessel;
    const matchC = filterCustomer === 'all' || s.customer === filterCustomer;
    return matchQ && matchTab && matchV && matchC;
  });

  useEffect(() => setCurrentPage(1), [search, activeTab, filterVessel, filterCustomer]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentItems = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleSelect = id => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = e => setSelected(e.target.checked ? filtered.map(s => s.id) : []);

  const inputBase = { background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '8px 11px', fontSize: 13, color: 'var(--text)', fontFamily: 'inherit', outline: 'none' };

  return (
    <div className="fade-up" style={{ padding: 24 }}>
      <SectionHead title="Stock List" subtitle="All warehouse items — filter, search, and create delivery orders">
        <Btn variant="primary" onClick={() => onNav('new-entry')}>
          <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Stock
        </Btn>
      </SectionHead>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 320 }}>
          <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }}
            width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search PO, vessel, AWB, description..."
            style={{ ...inputBase, paddingLeft: 32, width: '100%' }} />
        </div>
        <div style={{ flex: 1 }} />
        <select value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)} style={{ ...inputBase, width: 170 }}>
          <option value="all">All Companies</option>
          {customers.map(c => <option key={c}>{c}</option>)}
        </select>
        <select value={filterVessel} onChange={e => setFilterVessel(e.target.value)} style={{ ...inputBase, width: 170 }}>
          <option value="all">All Vessels</option>
          {vessels.map(v => <option key={v}>{v}</option>)}
        </select>

      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'flex', gap: 2, marginBottom: 16, background: 'var(--white)',
        border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 5, boxShadow: 'var(--shadow)'
      }}>
        {[
          { key: 'all', label: 'All Items', count: stock.length },
          { key: 'active', label: 'Active Stock', count: activeStock.length },
          { key: 'drafted', label: 'Drafts', count: stock.filter(s => s.status === 'drafted').length },
          { key: 'completed', label: 'Completed', count: completedStock.length },
          { key: 'offland', label: 'OffLand', count: stock.filter(s => s.type === 'Offland').length },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit', transition: 'all .15s',
              background: activeTab === t.key ? 'var(--accent)' : 'transparent',
              color: activeTab === t.key ? '#fff' : 'var(--muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
            }}>
            {t.label}
            {t.count !== undefined && <span style={{
              padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700,
              background: activeTab === t.key ? 'rgba(255,255,255,.25)' : 'var(--surface2)',
              color: activeTab === t.key ? '#fff' : 'var(--muted)'
            }}>{t.count}</span>}
          </button>
        ))}
      </div>

      <Card>
        <div style={{ overflowX: 'auto' }}>
          <Table
            headers={['', 'Created At', 'Company', 'Vessel', 'Location', 'Supplier', 'FRT', 'AWB/BL/LR', 'Port', 'PO Number', 'Qty', 'PKG', 'Wgt', 'Unit', 'Dimension', 'Description', 'Remarks', 'Status', 'Actions']}
            empty={filtered.length === 0 ? 'No items match your filters' : null}>
            {currentItems.map(s => (
              <Tr key={s.id}>
                <Td>
                  <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)}
                    style={{ width: 14, height: 14, cursor: 'pointer' }} />
                </Td>
                <Td>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{s.entry_date ? s.entry_date.split('T')[0] : s.date}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{s.time || '—'}</div>
                </Td>
                <Td strong>{s.customer}</Td>
                <Td strong>{s.vessel}</Td>
                <Td>{s.location ? <Chip>{s.location}</Chip> : '—'}</Td>
                <Td><span style={{ fontSize: 12 }}>{s.supplier}</span></Td>
                <Td><Chip variant="blue">{s.frtType || s.freight || '—'}</Chip></Td>
                <Td><span style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>{s.awb || '—'}</span></Td>
                <Td><span style={{ fontSize: 11 }}>{s.port || '—'}</span></Td>
                <Td strong><span style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>{s.po}</span></Td>
                <Td>{s.qty}</Td>
                <Td><span style={{ fontSize: 11 }}>{s.pkgType || 'PKG'}</span></Td>
                <Td>{s.weight}</Td>
                <Td><span style={{ fontSize: 11 }}>{s.unit || 'KGS'}</span></Td>
                <Td><span style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>{s.dimension || '—'}</span></Td>
                <Td><span style={{ fontSize: 12 }}>{s.description || '—'}</span></Td>
                <Td><span style={{ fontSize: 11, color: 'var(--muted)', maxWidth: 120, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.remarks || '—'}</span></Td>
                <Td><Badge status={s.status} /></Td>
                <Td>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: 'flex-end' }}>
                    {s.status === 'drafted' && (
                      <Btn size="xs" variant="warning" onClick={() => onNav('new-entry', s)}>✏ Resume</Btn>
                    )}
                    {s.status === 'active' && role === 'admin' && (
                      <span onClick={() => onNav('delivery')}
                        style={{ color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>→ DN</span>
                    )}
                    {s.status === 'rejected' && (
                      <Btn size="xs" variant="warning" onClick={() => onNav('new-entry')}>Fix</Btn>
                    )}
                    {role === 'admin' && (
                      <Btn size="xs" variant="primary" onClick={() => { 
                        setEditTarget(s); 
                        setEditData({ 
                          process_type: s.process_type || (s.type === 'Offland' ? 'offland' : 'incoming'),
                          customer_id: s.customer_id,
                          vessel_id: s.vessel_id,
                          supplier_id: s.supplier_id,
                          awb_bl_number: s.awb || '',
                          po_numbers: s.po || '',
                          quantity: s.qty || '',
                          weight: s.weight || '',
                          dimensions: s.dimension || '',
                          freight_type_id: s.freight_type_id,
                          origin_country_id: s.origin_country_id,
                          warehouse_location_id: s.warehouse_location_id,
                          port: s.port || '',
                          description: s.description || '',
                          remarks: s.remarks || '',
                          pkg_type: s.pkgType || 'PKG',
                          packaging_unit: s.unit || 'KGS'
                        }); 
                      }}>✎ Edit</Btn>
                    )}
                    {role === 'admin' && s.status === 'pending' && (
                      <Btn size="xs" variant="success" onClick={() => approveStock(s.id)}>✓</Btn>
                    )}
                    {role === 'admin' && s.status === 'pending' && (
                      <Btn size="xs" variant="danger" onClick={() => setRejectTarget(s)}>✕</Btn>
                    )}
                    {s.status === 'active' && role === 'admin' && (
                      <Btn size="xs" variant="outline" onClick={() => setCompleteTarget(s)}>Complete</Btn>
                    )}
                    {role === 'admin' && (
                      <button onClick={() => setConfirmDelete({ id: s.id, name: s.po })} title="Delete" style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                      </button>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </Table>
          
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
              <Btn variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>← Prev</Btn>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>
                Page {currentPage} of {totalPages}
              </div>
              <Btn variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next →</Btn>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
            <Btn variant="outline" size="sm" onClick={() => handleExportExcel(selected.length > 0 ? stock.filter(s => selected.includes(s.id)) : filtered)}>
              📊 Export Excel
            </Btn>
            <Btn variant="primary" size="sm" onClick={() => generateStocklistPDF(filtered)}>
              ↓ Export PDF
            </Btn>
          </div>
        </div>

        {/* Bulk action bar */}
        {selected.length > 0 && role === 'admin' && (
          <div style={{
            padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex',
            alignItems: 'center', gap: 10, background: 'var(--accent-light)'
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>
              {selected.length} item{selected.length !== 1 ? 's' : ''} selected
            </span>
            <Btn variant="primary" size="sm" onClick={() => { showToast('✓ Delivery Order created!', 'success'); onNav('delivery'); }}>
              🚚 Create Delivery Order
            </Btn>
            <Btn variant="outline" size="sm">↓ Export Selected</Btn>
            <Btn variant="success" size="sm" onClick={() => { setIsBulk(true); setCompleteTarget(stock.find(s => selected.includes(s.id))); }}>
              ✓ Bulk Complete
            </Btn>
            <Btn variant="ghost" size="sm" style={{ marginLeft: 'auto' }} onClick={() => setSelected([])}>Clear</Btn>
          </div>
        )}
      </Card>

      {/* Reject Modal */}
      <Modal open={!!rejectTarget} onClose={() => { setRejectTarget(null); setRejectReason(''); }}
        title="Reject Entry" icon="✕" iconBg="var(--danger-bg)"
        footer={<>
          <Btn variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>Cancel</Btn>
          <Btn variant="danger" onClick={() => {
            if (!rejectReason.trim()) { showToast('Enter rejection reason', 'error'); return; }
            rejectStock(rejectTarget.id, rejectReason);
            setRejectTarget(null); setRejectReason('');
          }}>✕ Reject & Notify</Btn>
        </>}>
        <FormGroup label="Rejection Reason" required>
          <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder="Explain what needs to be corrected..." style={{ minHeight: 100 }} />
        </FormGroup>
      </Modal>

      {/* Complete Modal — Real Photo Upload + Signed Proof + Documents */}
      <Modal open={!!completeTarget} onClose={() => { setCompleteTarget(null); setCompletePhotos([]); setCompleteDocs([]); setSignedBy(''); setIsBulk(false); }}
        title={isBulk ? `Bulk Complete (${selected.length} items)` : "Mark as Completed"} 
        subtitle={isBulk ? "Upload proof for all selected items" : "Upload delivery proof and confirmation"}
        icon="✅" iconBg="var(--success-bg)"
        footer={<>
          <Btn variant="outline" onClick={() => { setCompleteTarget(null); setCompletePhotos([]); setCompleteDocs([]); setSignedBy(''); setIsBulk(false); }}>Cancel</Btn>
          <Btn variant="success" onClick={async () => {
            if (completePhotos.length === 0) { showToast('Upload at least 1 photo as delivery proof', 'error'); return; }
            if (!signedBy.trim()) { showToast('Enter who signed the proof of delivery', 'error'); return; }
            
            const targets = isBulk ? selected : [completeTarget.id];
            
            showToast(`Processing ${targets.length} items...`, 'info');
            
            for (const targetId of targets) {
              const fd = new FormData();
              fd.append('signed_by', signedBy);
              completePhotos.forEach(p => fd.append('photos', p.file));
              completeDocs.forEach(d => fd.append('photos', d.file));
              await completeStock(targetId, fd);
            }
            
            setCompleteTarget(null); setCompletePhotos([]); setCompleteDocs([]); setSignedBy(''); setIsBulk(false);
            if (isBulk) setSelected([]);
          }}>✓ Mark {isBulk ? 'All' : ''} Complete</Btn>
        </>}>
        <InfoBanner>📷 {isBulk ? 'Proof uploaded here will be applied to ALL selected items.' : 'Photo evidence and signed proof are mandatory to mark cargo as delivered.'}</InfoBanner>
        {!isBulk && completeTarget && (
          <div style={{ margin: '12px 0', padding: 12, background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{completeTarget.vessel} — {completeTarget.po}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{completeTarget.supplier} · {completeTarget.qty} {completeTarget.pkgType || 'PKG'} · {completeTarget.weight} {completeTarget.unit || 'KGS'}</div>
          </div>
        )}
        <FormGroup label="Photo Evidence" required hint="Upload photos of delivered cargo from gallery">
          <input type="file" id="completePhotoInput" accept="image/*" multiple
            style={{ display: 'none' }} onChange={handleCompletePhotoUpload} />
          {completePhotos.length === 0 ? (
            <label htmlFor="completePhotoInput"
              style={{
                display: 'block', border: '2px dashed var(--border2)', borderRadius: 10, padding: '18px 16px',
                textAlign: 'center', cursor: 'pointer', transition: 'all .2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-light)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontSize: 24 }}>📷</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Click to upload delivery photos from gallery</div>
            </label>
          ) : (
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                {completePhotos.map(p => (
                  <div key={p.id} style={{
                    width: 70, height: 70, borderRadius: 8, overflow: 'hidden',
                    border: '1.5px solid var(--success-mid)', position: 'relative', cursor: 'pointer'
                  }}
                    onMouseEnter={e => e.currentTarget.querySelector('.cdel').style.display = 'flex'}
                    onMouseLeave={e => e.currentTarget.querySelector('.cdel').style.display = 'none'}>
                    <img src={p.preview} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0, padding: '1px 3px',
                      background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 7.5, textAlign: 'center',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>{p.name}</div>
                    <div className="cdel" onClick={() => removeCompletePhoto(p.id)}
                      style={{
                        display: 'none', position: 'absolute', inset: 0, background: 'rgba(185,28,28,.8)',
                        borderRadius: 7, alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 18, fontWeight: 700
                      }}>✕</div>
                  </div>
                ))}
                {completePhotos.length < 10 && (
                  <label htmlFor="completePhotoInput" style={{
                    width: 70, height: 70, borderRadius: 8, border: '2px dashed var(--border2)',
                    background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, color: 'var(--muted)', cursor: 'pointer', transition: 'all .15s'
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--muted)'; }}>
                    +
                  </label>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{completePhotos.length} photo(s) uploaded</div>
            </div>
          )}
        </FormGroup>

        {/* Document Proof Portion Exactly like New Entry / DN Modal */}
        <div style={{ marginTop: 24, borderTop: '1px solid var(--border)', paddingTop: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            📄 Document Proof (Optional) <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>PDF, Word, Excel etc.</span>
          </div>
          <input type="file" id="completeDocInput" accept=".pdf,.doc,.docx,.xls,.xlsx" multiple
            style={{ display: 'none' }} onChange={handleCompleteDocUpload} />
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {completeDocs.map(d => (
              <div key={d.id} style={{
                padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8,
                border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10,
                position: 'relative'
              }}>
                <span style={{ fontSize: 18 }}>📄</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                  <div style={{ fontSize: 9, color: 'var(--muted)' }}>{d.size}</div>
                </div>
                <div onClick={() => removeCompleteDoc(d.id)} style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 800, fontSize: 14, padding: '0 4px' }}>✕</div>
              </div>
            ))}
            <label htmlFor="completeDocInput" style={{
              padding: '10px 16px', borderRadius: 8, border: '2px dashed var(--border2)',
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600,
              color: 'var(--muted)', cursor: 'pointer', transition: 'all .2s'
            }}>
              <span>+ Add Document Proof</span>
            </label>
          </div>
        </div>

        <FormGroup label="Signed / Received By" required hint="Name of person who signed delivery proof">
          <Input value={signedBy} onChange={e => setSignedBy(e.target.value)} placeholder="e.g. Capt. Ahmad — MV Prima Energy" />
        </FormGroup>
      </Modal>

      {/* Confirm Permanent Delete Modal */}
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)}
        title="Delete Stock Entry" subtitle="This action cannot be undone."
        icon="🗑" iconBg="#fee2e2"
        footer={<>
          <Btn variant="outline" onClick={() => setConfirmDelete(null)}>No, Keep It</Btn>
          <Btn variant="danger" onClick={() => { deleteStock(confirmDelete.id); setConfirmDelete(null); }}>Yes, Delete Item</Btn>
        </>}>
        <div style={{ padding: '10px 0' }}>
          Are you sure you want to delete stock entry <b>{confirmDelete?.name}</b> permanently?
          This will remove it from all warehouse records.
        </div>
      </Modal>

      {/* Edit Admin Modal */}
      <Modal open={!!editTarget} onClose={() => setEditTarget(null)}
        title="Edit Stock Entry" subtitle="Full Admin Access — Direct Database Management"
        icon="✎" iconBg="var(--accent-light)" wide
        footer={<>
          <Btn variant="outline" onClick={() => setEditTarget(null)}>Cancel</Btn>
          <Btn variant="success" onClick={() => {
            updateStock(editTarget.id, editData);
            setEditTarget(null);
          }}>Save Changes</Btn>
        </>}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          <FormGroup label="Process Type" required>
            <select value={editData.process_type} onChange={e => setEditData({...editData, process_type: e.target.value})} style={inputBase}>
              <option value="incoming">Incoming</option>
              <option value="offland">Offland</option>
            </select>
          </FormGroup>
          <FormGroup label="Customer" required>
            <select value={editData.customer_id} onChange={e => setEditData({...editData, customer_id: e.target.value})} style={inputBase}>
              <option value="">Select Customer</option>
              {allCustomers.map(c => <option key={c.id} value={c.id}>{c.company}</option>)}
            </select>
          </FormGroup>
          <FormGroup label="Vessel" required>
            <select value={editData.vessel_id} onChange={e => setEditData({...editData, vessel_id: e.target.value})} style={inputBase}>
              <option value="">Select Vessel</option>
              {allVessels.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </FormGroup>
          <FormGroup label="Supplier" required>
            <select value={editData.supplier_id} onChange={e => setEditData({...editData, supplier_id: e.target.value})} style={inputBase}>
              <option value="">Select Supplier</option>
              {allSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </FormGroup>
          <FormGroup label="AWB / BL / LR Number">
            <Input value={editData.awb_bl_number} onChange={e => setEditData({...editData, awb_bl_number: e.target.value})} placeholder="AWB-1234"/>
          </FormGroup>
          <FormGroup label="PO Numbers">
            <Input value={editData.po_numbers} onChange={e => setEditData({...editData, po_numbers: e.target.value})} placeholder="PO-1234, PO-5678"/>
          </FormGroup>
          <div style={{ display: 'flex', gap: 10 }}>
            <FormGroup label="Quantity" style={{flex: 1}}>
              <Input type="number" value={editData.quantity} onChange={e => setEditData({...editData, quantity: e.target.value})} />
            </FormGroup>
            <FormGroup label="Pkg Type" style={{flex: 1}}>
              <Input value={editData.pkg_type} onChange={e => setEditData({...editData, pkg_type: e.target.value})} />
            </FormGroup>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <FormGroup label="Weight" style={{flex: 1}}>
              <Input type="number" value={editData.weight} onChange={e => setEditData({...editData, weight: e.target.value})} />
            </FormGroup>
            <FormGroup label="Unit" style={{flex: 1}}>
              <Input value={editData.packaging_unit} onChange={e => setEditData({...editData, packaging_unit: e.target.value})} />
            </FormGroup>
          </div>
          <FormGroup label="Dimensions" hint="L x W x H cm">
            <Input value={editData.dimensions} onChange={e => setEditData({...editData, dimensions: e.target.value})} placeholder="100x100x100"/>
          </FormGroup>
          <FormGroup label="Freight Type">
            <select value={editData.freight_type_id} onChange={e => setEditData({...editData, freight_type_id: e.target.value})} style={inputBase}>
              <option value="">Select Freight Type</option>
              {allFreight.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </FormGroup>
          <FormGroup label="Origin Country">
            <select value={editData.origin_country_id} onChange={e => setEditData({...editData, origin_country_id: e.target.value})} style={inputBase}>
              <option value="">Select Country</option>
              {allCountries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FormGroup>
          <FormGroup label="Warehouse Location">
            <select value={editData.warehouse_location_id} onChange={e => setEditData({...editData, warehouse_location_id: e.target.value})} style={inputBase}>
              <option value="">Select Location</option>
              {allLocations.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
            </select>
          </FormGroup>
          <FormGroup label="Port">
            <Input value={editData.port} onChange={e => setEditData({...editData, port: e.target.value})} />
          </FormGroup>
          <FormGroup label="Description" full>
            <Input value={editData.description} onChange={e => setEditData({...editData, description: e.target.value})} />
          </FormGroup>
          <FormGroup label="Remarks" full>
            <Textarea value={editData.remarks} onChange={e => setEditData({...editData, remarks: e.target.value})} placeholder="Any special notes..." />
          </FormGroup>
        </div>
      </Modal>
    </div>
  );

  function handleExportExcel(data) {
    const ws = XLSX.utils.json_to_sheet(data.map(s => ({
      Date: s.date,
      Vessel: s.vessel,
      Customer: s.customer,
      PO_Number: s.po,
      AWB_BL: s.awb,
      Description: s.description,
      Qty: s.qty,
      Unit: s.unit || 'KGS',
      Weight: s.weight,
      Location: s.location || '—',
      Status: s.status
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock List");
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    saveAs(blob, `CargoFlow_StockList_${new Date().toISOString().split('T')[0]}.xlsx`);
  }
}
